import type { IDataState } from "@/store/modules/data";
import { DataModules } from "@/store/modules/data/modules";
import type { ActionTree, GetterTree } from "vuex";
import type { IState } from "@/store";
import _ from "@/boot/lodash";
import { ToastProgrammatic as $toast } from "buefy";
import i18n from "@/i18n";
import type { ICompany } from "@upmind-automation/types";
import type { ApiPathGetter } from "@/models/api";

const initialState = {} as IDataState;

const getters: GetterTree<IDataState, IState> = {
  apiPath:
    (s, g, rS, { isAdminContext, isMockClientContext }): ApiPathGetter =>
    clientId => {
      const admin = `api/admin/clients/${clientId}/companies`;
      const client = `api/clients/${clientId}/companies`;
      const contextual =
        isAdminContext && !isMockClientContext() ? admin : client;
      return { client, admin, contextual };
    },
  clientScope: () => id => {
    return `$client_${id}`;
  },
  selectorScope: () => id => {
    return `$client_${id}_selector`;
  },
  params: (state: IDataState, getters) => (id: ICompany["id"]) => {
    return _.get(state, `${getters.clientScope(id)}.params`, {});
  }
};

const actions: ActionTree<IDataState, IState> = {
  list: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/list",
      {
        ...payload,
        path: getters.apiPath(payload?.id).contextual,
        storeModule: DataModules.CLIENTS_COMPANIES,
        params: {
          ...payload.params,
          with_staged_imports: 1
        }
      },
      { root: true }
    );
  },
  create: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/create",
      {
        ...payload,
        path: getters.apiPath(payload?.id).contextual,
        storeModule: DataModules.CLIENTS_COMPANIES
      },
      { root: true }
    );
  },
  update: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/update",
      {
        ...payload,
        path: `${getters.apiPath(payload?.id).contextual}/${
          payload?.company_id
        }`,
        storeModule: DataModules.CLIENTS_COMPANIES
      },
      { root: true }
    );
  },
  remove: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/remove",
      {
        ...payload,
        path: `${getters.apiPath(payload?.id).contextual}/${
          payload?.company_id
        }`,
        storeModule: DataModules.CLIENTS_COMPANIES
      },
      { root: true }
    );
  },
  openCompanyModal: ({ dispatch }, config) => {
    return dispatch(
      "ui/open/modal",
      {
        config: {
          component: () =>
            import(
              "@/components/app/global/client/addEditClientCompanyModal.vue"
            ),
          width: 640,
          ...config
        }
      },
      { root: true }
    );
  },
  openEditModal: (
    { dispatch },
    { company, config = {} }: { company: ICompany; config: any }
  ) => {
    return dispatch("openCompanyModal", {
      props: {
        data: _.pick(company, [
          "name",
          "vat_number",
          "reg_number",
          "address_id",
          "email_id",
          "phone_id"
        ]),
        clientId: company.client_id,
        actionType: "update",
        companyId: company.id
      },
      width: 640,
      ...config
    });
  },
  confirmDelete: async (
    { dispatch, rootGetters },
    {
      company,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onConfirm = () => {},
      onSuccess = () => {
        $toast.open({
          message: i18n.t("_.object_successfully_removed", {
            object: i18n.t("_.company")
          }) as string
        });
      },
      onError = error => {
        dispatch("api/handleValidationError", { error }, { root: true });
      }
    }: {
      company: ICompany;
      onConfirm: Function;
      onSuccess: Function;
      onError: Function;
    }
  ) => {
    if (!rootGetters["user/can"]("delete_client_company")) return;
    const confirm = await dispatch(
      "ui/open/confirmModal",
      {
        config: {
          props: {
            autoClose: false,
            mode: "button",
            title: i18n.t("_action.delete_object", {
              object: (i18n.t("_.company") as string).toLowerCase()
            }),
            confirmButtonText: i18n.t("_action.delete"),
            message: i18n.t("_sentence.delete_confirmation", {
              object: (i18n.t("_.company") as string).toLowerCase()
            })
          },
          events: {
            confirmed: async () => {
              try {
                await dispatch("remove", {
                  id: company.client_id,
                  company_id: company.id
                });
                await onSuccess();
              } catch (error) {
                await onError(error);
              }
              await onConfirm();
              confirm.close();
            }
          }
        }
      },
      { root: true }
    );
  },
  makeDefault: async (
    { dispatch, rootGetters },
    {
      company,
      onSuccess = () =>
        $toast.open({
          message: i18n.t("_.default_object_updated", {
            object: (i18n.t("_.company") as string).toLowerCase()
          }) as string
        }),
      onError = (error: Error) =>
        dispatch("api/handleValidationError", { error }, { root: true })
    }: {
      company: ICompany;
      onSuccess: Function;
      onError: Function;
    }
  ) => {
    if (!rootGetters["user/can"]("update_client_company")) return;
    try {
      await dispatch("update", {
        id: company.client_id,
        company_id: company.id,
        data: { default: true }
      });
      await onSuccess();
      return Promise.resolve(true);
    } catch (error) {
      await onError(error);
      return;
    }
  }
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions
};
